<template>
    <div>
        <div class="flex flex-col lg:flex-row">
            <div class="w-full">
                <div class="flex justify-between items-center mb-4">
                    <div class="flex flex-1 items-center">
                        <button @click="manageScenarios" class="mb-0 mr-2 uppercase text-sm text-blue-500 font-semibold underline">Scenario</button>
                        <select name="scenario" class="mt-0 max-w-fit form-select form-select-blue" v-model="ksds.selectedScenarioId">
                            <option v-for="(scenario, index) in scenarios" :key="scenario.id" :value="scenario.id" :selected="index === 0">{{ scenario.name }}</option>
                        </select>
                        <div v-if="editing" class="ml-3">
                            
                        </div>
                    </div>
                    <div class="flex items-center space-x-2">
                        <div v-if="!editing && canEditKSDs" @click="editing = true" class="cursor-pointer">
                            <inline-svg name="edit-pencil" classes="w-7 h-7"></inline-svg>
                        </div>
                        <button v-if="editing && canEditKSDs" @click="addKSD" type="button" class="btn btn-small btn-blue flex items-center space-x-2">
                            <inline-svg name="add" classes="w-4 h-4 text-white fill-current"></inline-svg>
                            <p class="text-white">Add</p>
                        </button>
                    </div>
                </div>

                <div class="w-full">
                    <div v-if="scenarioKsds.length">
                        <table class="w-full">
                            <thead>
                                <tr>
                                    <th v-if="editing" class="pl-4">Sort</th>
                                    <th :class="{ 'pl-4': ! editing }">Driver</th>
                                    <th class="text-right">Weight (%)</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <draggable :list="scenarioKsds" :element="'tbody'" :options="{disabled: ! editing, filter: 'input', preventOnFilter: false}" @end="updateSortOrders">
                                <tr v-for="ksd in scenarioKsds" :key="ksd.id">
                                    <td class="pl-4" v-if="editing">
                                        <inline-svg name="sort"></inline-svg>
                                        <input type="hidden" name="sort_order" v-model="ksd.sort_order">
                                    </td>
                                    <td :class="{ 'pl-4': ! editing }">
                                        <div v-if="! editing || (editing && ksd.driver == 'Price')" class="text-sm" style="min-width: 200px;">{{ ksd.driver }}</div>
                                        <input v-if="editing && ksd.driver != 'Price'" v-model="ksd.driver" type="text" class="form-input">
                                    </td>
                                    <td class="text-right">
                                        <div v-if="! editing" class="text-sm">{{ ksd.weight }}%</div>
                                        <input v-if="editing" v-model="ksd.weight" type="number" min="0" max="80" step="5">
                                    </td>
                                    <td>
                                        <div
                                            v-if="editing && ksd.driver != 'Price' && canDeleteKSDs"
                                            @click="confirmDeleteKSD(ksd.id)"
                                            class="flex items-center justify-center cursor-pointer">
                                                <inline-svg name="trash"></inline-svg>
                                        </div>
                                    </td>
                                </tr>
                            </draggable>
                            <tfoot class="border-t border-gray-300">
                                <tr>
                                    <td :colspan="editing ? 2 : 1" class="text-sm font-medium pl-6">Unallocated Percentage</td>
                                    <td class="text-right text-sm">{{ unallocatedPercentage }}%</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                        <div v-if="editing" class="flex flex-col">
                            <div class="mt-3">
                                <div class="flex justify-end items-center">
                                    <button @click="editing = false" class="btn btn-secondary ml-6 mr-3">Cancel</button>
                                    <button v-if="canEditKSDs" @click="saveKSDs" class="btn btn-green">Save</button>
                                </div>
                            </div>
                            <div>
                                <div class='text-xs text-white font-semibold bg-yellow-50 border-l-4 border-yellow-200 p-3 my-3 leading-normal'>
                                    <span style="color: hsla(54, 100%, 28%, 1);">Changing KSD's will result in a recalculation of scores for every Comp in your project</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="mt-10">
                        <p class="m-6 text-2xl text-center">No KSDs Found</p>
                    </div>
                </div>
            </div>

            <div v-if="showChart" class="h-full w-full lg:w-2/3">
                <pie-chart :data="chartData" height="480" ref="ksdChart"></pie-chart>
            </div>
        </div>

        <modal name="manage-scenarios" width="60%" height="auto" click-to-close draggable=".modal-header">
            <manage-scenarios
                :ksdable-id="ksdableId"
                :ksdable-type="ksdableType"
                :scenarios="scenarios"
                :scenario="scenario">
            </manage-scenarios>
        </modal>
    </div>
</template>

<script>
    import draggable from 'vuedraggable';
    import { mapGetters, mapState } from 'vuex';

    export default {
        name: 'ReoptKsds',

        props: {
            ksdableId: {
                type: Number,
                required: true
            },

            ksdableType: {
                type: String,
                required: true
            },

            showChart: {
                type: Boolean,
                default: false
            }
        },

        components: {
            draggable
        },

        data() {
            return { 
                editing: false
            }
        },

        mounted() {
            this.ksds.selectedScenarioId = this.ksds.scenarios[0]?.id || null;
        },

        computed: {
            ...mapState(['ksds']),
            ...mapGetters(['scenario', 'scenarioKsds']),

            canEditKSDs() {
                return this.$gate.can('update', 'project', this.ksdableId);
            },
            
            canDeleteKSDs() {
                return this.$gate.can('delete', 'project', this.ksdableId);
            },

            scenarios() {
                return this.ksds.scenarios;
            },

            chartData() {
                return _.map(this.scenarioKsds, ksd => {
                    return { label: ksd.driver, value: ksd.weight };
                });
            },

            unallocatedPercentage() {
                return 100 - parseInt(_.sumBy(this.scenarioKsds, (ksd) => { return parseInt(ksd.weight); }));
            }
        },

        watch: {
            chartData: function () {
                this.updateChart();
            }
        },

        methods: {
            updateChart() {
                const chart = this.$refs.ksdChart

                if (chart) {
                    chart.dataSource.data = this.chartData;
                }
            },

            updateSortOrders() {
                _.map(this.scenario.ksds, (ksd, index) => {
                    ksd.sort_order = index;
                });
            },

            manageScenarios() {
                this.$modal.show('manage-scenarios');
            },

            addKSD() {
                this.scenario.ksds.push({
                    scenario_id: this.ksds.selectedScenarioId,
                    ksdable_id: this.ksdableId,
                    ksdable_type: this.ksdableType,
                    driver: '',
                    sort_order: this.scenario.ksds.length,
                    weight: 0
                });
            },

            removeKSD(id) {
                this.scenario.ksds = _.remove(this.scenario.ksds, (ksd) => { return ksd.id != id });
            },

            confirmDeleteKSD(id) {
                if (typeof id == "undefined") {
                    this.scenario.ksds.splice(-1,1);
                    
                    return;
                }

                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this KSD?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteKSD(id);
                    }
                });
            },

            deleteKSD(id) {
                axios.delete('/ksds/' + id)
                    .then((response) => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: response.data.message
                        });

                        this.removeKSD(id);
                    })
                    .catch((error) => {
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response.data.message
                        });
                    }
                );
            },

            saveKSDs() {
                if (this.unallocatedPercentage < 0) {
                    return this.$swal({
                        title: 'Invalid Weights',
                        text: "Total weight cannot exceed 100%",
                        type: 'warning',
                        buttonsStyling: false,
                        confirmButtonText: 'OK',
                        confirmButtonClass: 'btn btn-green',
                    });
                }

                axios.patch('/ksds/scenarios/' + this.scenario.id, this.scenario).then((response) => {
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    });

                    this.editing = false;   
                    this.scenario.ksds = response.data.scenario.ksds;

                }).catch((response) => {
                    console.error(response);
                })
            }
        },
    }
</script>